import React from 'react';
import styled from 'styled-components';
import { DateDropdown, SearchInput } from '@dev/ee-components';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import dayjs from '@/services/tools/dayjs';
import { debounce } from '@/services/tools/debounce';
import { DEFAULT_OFFSET, LONG_DATE_FORMAT, DEFAULT_ACTIVE_PAGE } from '@/services/constants';
import { GetAllCheckoutsStart } from '@/store/checkouts/actions';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { useDateFilterData } from '@/services/tools/date';
import { useDateDropdownValue } from '@/services/hooks/useDateDropdownValue';
import { useCheckoutParamsFromUrl } from '@/components/Checkouts/hooks/useRequestParams';

const SearchInputWrapper = styled.div`
    width: 280px;
`;

const TableFiltersWrapper = styled.div`
    display: flex;
    flex-direction: row;
    padding: 0px 48px 34px;
    justify-content: space-between;
`;

export const TableFilters = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const params = useCheckoutParamsFromUrl();
    const dateDropdownValue = useDateDropdownValue(false);


    const handleCalendarChange = (startDate?: string, endDate?: string) => {
        dispatch(
            GetAllCheckoutsStart({
                ...params,
                to: endDate,
                from: dayjs(startDate).startOf('day').format(),
                offset: DEFAULT_OFFSET,
                page: DEFAULT_ACTIVE_PAGE
            })
        );
    }

    const handleItemClick = (ev, value?: Record<string, string | number>) => {
        dispatch(
            GetAllCheckoutsStart({
                ...params,
                to: dayjs().endOf('day').format(LONG_DATE_FORMAT),
                from:
                    value?.value === DateDropdownOptions.All
                        ? null
                        : dayjs()
                              .subtract(value?.value as number, 'day')
                              .startOf('day')
                              .format(LONG_DATE_FORMAT),
                offset: DEFAULT_OFFSET,
                page: DEFAULT_ACTIVE_PAGE
            })
        );
    }

    const handleSearchChange = (event): void => {
        const searchString = event?.target?.value;
        debounce((): void => {
            dispatch(
                GetAllCheckoutsStart({
                    ...params,
                    search: searchString,
                    offset: DEFAULT_OFFSET,
                    page: DEFAULT_ACTIVE_PAGE
                })
            );
        }, 500);
    }

    const handleSearchDelete = (): void => {
        dispatch(
            GetAllCheckoutsStart({
                ...params,
                search: '',
                offset: DEFAULT_OFFSET,
                page: DEFAULT_ACTIVE_PAGE
            })
        );
    }
    
    return (
        <TableFiltersWrapper>
            <DateDropdown
                options={useDateFilterData()}
                defaultPlaceholder={t('all')}
                type="input"
                returnRawData
                value={dateDropdownValue}
                calendarOnChange={handleCalendarChange}
                onItemClick={handleItemClick}
                buttonVariant="tertiary"
                startDate={params.from?.toString()}
                endDate={params.to?.toString()}
            />
            <SearchInputWrapper>
                <SearchInput
                    id="searchCheckouts"
                    name="search"
                    placeholder={t('searchforcheckouts')}
                    type="text"
                    onChange={handleSearchChange}
                    onDelete={handleSearchDelete}
                    defaultValue={params.search}
                />
            </SearchInputWrapper>
        </TableFiltersWrapper>
    );
};
